// One hole, as it looks on a phone clipped to a cart in full sun.
//
// Everything about this component is a concession to where it is used. The stepper buttons
// are 76px because they are pressed with a glove on, on a moving cart, one-handed. The
// score is enormous because it is read at arm's length with the sun behind it. There is no
// text input for a score anywhere: a keyboard on a golf course is a way to type 55 instead
// of 5 and not notice until the scorer's table.
//
// The contest banner sits ABOVE the steppers on purpose. Knowing hole 7 has a closest to
// the pin BEFORE you hit is the entire value of putting contests in the app — told
// afterwards it is trivia, told beforehand it changes the shot.

import { useEffect, useState } from 'react';
import type { ContestKind } from '@shared/types';
import { CONTEST_LABELS } from '@shared/engine/contests';
import WolfPick, { type WolfPickBlock, type WolfPickValue } from './WolfPick';

/** One scorable line. A scramble has exactly one of these; an individual event has one per player. */
export type Stepper = {
  entrantId: string;
  /** "Rick Akers", or "Open spot 3" for a seat sold but never filled in. */
  name: string;
  /** null = nothing entered yet. Not zero, not par — genuinely blank. */
  value: number | null;
  /** True while the write for this line is still trying to reach the server. */
  unsaved: boolean;
};

export type HoleCardContest = {
  /** Null when the contest rows could not be read, which makes claiming impossible. */
  contestId: string | null;
  kind: ContestKind;
  /** The host's own wording if they gave one, else the standard name. */
  label: string;
  /** The claim this group has already recorded, if any. */
  claim: { entrantId: string; measure: string } | null;
  unsaved: boolean;
};

export type ContestEntrant = { id: string; name: string };

type Props = {
  /** 1-based and already offset, so a back nine reads 10–18 like the signs do. */
  holeNumber: number;
  par: number | null;
  /** Where this hole falls in THIS group's round — 3 of 18 for a shotgun off hole 7. */
  playPosition: number;
  holeCount: number;
  /** "Team score" in a scramble — the one number four people made together. */
  scramble: boolean;
  steppers: Stepper[];
  /** null clears the hole. A cleared hole is a DELETE, never a zero. */
  onScore: (entrantId: string, next: number | null) => void;
  contests: HoleCardContest[];
  /** Who can be credited with a contest — the people on this card. */
  contestEntrants: ContestEntrant[];
  onClaim: (contestId: string, entrantId: string, measure: string) => void;
  onClearClaim: (contestId: string) => void;
  /**
   * One per registered Wolf game, already resolved for this hole — whose turn it is comes
   * from ScorePage, which mirrors the engine's rotation. Empty when nobody is playing Wolf.
   */
  wolfBlocks: WolfPickBlock[];
  /** null clears the hole's pick. */
  onWolfPick: (gameId: string, next: WolfPickValue | null) => void;
  /** Shown under the card so the group knows what is on the next tee box. */
  upcoming: { holeNumber: number; labels: string[] } | null;
};

/** The DB check constraint is `gross between 1 and 30`; refuse to send anything it will reject. */
const MIN_GROSS = 1;
const MAX_GROSS = 30;

/** Golf shorthand, for the small line under the score. */
function relativeToPar(gross: number, par: number | null): string | null {
  if (par == null) return null;
  const d = gross - par;
  if (d <= -3) return 'Albatross';
  if (d === -2) return 'Eagle';
  if (d === -1) return 'Birdie';
  if (d === 0) return 'Par';
  if (d === 1) return 'Bogey';
  return `+${d}`;
}

/** Under par is red, over par is blue — the colours already in the theme. */
function scoreTone(gross: number, par: number | null): string {
  if (par == null) return 'var(--ink)';
  if (gross < par) return 'var(--birdie)';
  if (gross > par) return 'var(--bogey)';
  return 'var(--ink)';
}

function ContestBlock({
  contest,
  entrants,
  onClaim,
  onClearClaim,
}: {
  contest: HoleCardContest;
  entrants: ContestEntrant[];
  onClaim: (contestId: string, entrantId: string, measure: string) => void;
  onClearClaim: (contestId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [who, setWho] = useState('');
  const [measure, setMeasure] = useState('');

  // Re-seed the form whenever the recorded claim changes underneath it — including when
  // the hole changes and this component is reused for a different contest.
  useEffect(() => {
    setWho(contest.claim?.entrantId ?? '');
    setMeasure(contest.claim?.measure ?? '');
    setOpen(false);
  }, [contest.contestId, contest.claim?.entrantId, contest.claim?.measure]);

  const claimedBy = contest.claim
    ? entrants.find((e) => e.id === contest.claim?.entrantId)?.name ?? 'Somebody on this card'
    : null;

  return (
    <div className="hc-contest">
      <div className="hc-contest-head">
        <span className="hc-contest-kind">{CONTEST_LABELS[contest.kind]}</span>
        {contest.unsaved ? <span className="hc-dot" title="Not saved yet" /> : null}
      </div>
      {contest.label && contest.label !== CONTEST_LABELS[contest.kind] ? (
        <div className="hc-contest-label">{contest.label}</div>
      ) : null}

      {contest.claim ? (
        <div className="hc-claim-current">
          <strong>{claimedBy}</strong>
          {contest.claim.measure ? <span className="tnum"> — {contest.claim.measure}</span> : null}
          <div className="hc-claim-caveat">
            Recorded as your foursome&rsquo;s claim. The host compares every foursome and decides.
          </div>
        </div>
      ) : null}

      {contest.contestId == null ? (
        <div className="hc-claim-caveat">
          Claims cannot be recorded right now &mdash; tell the scorer&rsquo;s table instead.
        </div>
      ) : !open ? (
        <button type="button" className="hc-claim-open" onClick={() => setOpen(true)}>
          {contest.claim ? 'Change our claim' : 'We got this one'}
        </button>
      ) : (
        <div className="hc-claim-form">
          <label htmlFor={`hc-who-${contest.contestId}`}>Who</label>
          <select
            id={`hc-who-${contest.contestId}`}
            value={who}
            onChange={(e) => setWho(e.target.value)}
          >
            <option value="">Pick a player</option>
            {entrants.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
              </option>
            ))}
          </select>

          <label htmlFor={`hc-measure-${contest.contestId}`}>Measurement</label>
          <input
            id={`hc-measure-${contest.contestId}`}
            value={measure}
            inputMode="text"
            placeholder="14 ft 3 in"
            onChange={(e) => setMeasure(e.target.value)}
          />

          <div className="hc-claim-actions">
            <button
              type="button"
              disabled={!who}
              onClick={() => {
                if (!contest.contestId || !who) return;
                onClaim(contest.contestId, who, measure.trim());
                setOpen(false);
              }}
            >
              Save claim
            </button>
            <button type="button" className="secondary" onClick={() => setOpen(false)}>
              Cancel
            </button>
            {contest.claim ? (
              <button
                type="button"
                className="danger"
                onClick={() => {
                  if (!contest.contestId) return;
                  onClearClaim(contest.contestId);
                  setOpen(false);
                }}
              >
                Remove
              </button>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

export default function HoleCard({
  holeNumber,
  par,
  playPosition,
  holeCount,
  scramble,
  steppers,
  onScore,
  contests,
  contestEntrants,
  onClaim,
  onClearClaim,
  wolfBlocks,
  onWolfPick,
  upcoming,
}: Props) {
  // An empty line starts at par on the first tap. Par is shown greyed in the box before
  // anyone touches it, so the number that appears is the number they were already looking
  // at rather than a surprise.
  const bump = (s: Stepper, delta: number) => {
    const base = s.value ?? (par ?? 4) - (delta > 0 ? 0 : 1);
    const next = s.value == null ? base : s.value + delta;
    onScore(s.entrantId, Math.max(MIN_GROSS, Math.min(MAX_GROSS, next)));
  };

  return (
    <div className="hc">
      <style>{CSS}</style>

      <div className="hc-head">
        <div className="hc-hole">
          <span className="hc-hole-word">Hole</span>
          <span className="hc-hole-num tnum">{holeNumber}</span>
        </div>
        <div className="hc-head-right">
          {par != null ? <div className="hc-par tnum">Par {par}</div> : null}
          <div className="hc-pos tnum">
            {playPosition} of {holeCount} for your foursome
          </div>
        </div>
      </div>

      {contests.map((c) => (
        <ContestBlock
          key={c.contestId ?? `${c.kind}-${holeNumber}`}
          contest={c}
          entrants={contestEntrants}
          onClaim={onClaim}
          onClearClaim={onClearClaim}
        />
      ))}

      {/* Above the steppers for the same reason the contest banner is: a Wolf pick is made
          on the tee box, before there is a number to write down. Below the contest, because
          a sponsored closest-to-the-pin is the group's one shot at it and this is not. */}
      {wolfBlocks.map((block) => (
        <WolfPick key={block.gameId} block={block} onPick={onWolfPick} />
      ))}

      {steppers.map((s) => {
        const rel = s.value != null ? relativeToPar(s.value, par) : null;
        return (
          <div className="hc-line" key={s.entrantId}>
            <div className="hc-line-name">
              <span className="hc-name">{scramble ? 'Foursome score' : s.name}</span>
              {s.unsaved ? (
                <span className="hc-unsaved">
                  <span className="hc-dot" /> not saved yet
                </span>
              ) : null}
            </div>

            <div className="hc-stepper">
              <button
                type="button"
                className="hc-step"
                aria-label={`One less for ${scramble ? 'the foursome' : s.name}`}
                onClick={() => bump(s, -1)}
                disabled={s.value != null && s.value <= MIN_GROSS}
              >
                &minus;
              </button>

              <div className="hc-value">
                {s.value != null ? (
                  <>
                    <span className="hc-num tnum" style={{ color: scoreTone(s.value, par) }}>
                      {s.value}
                    </span>
                    {rel ? <span className="hc-rel">{rel}</span> : null}
                  </>
                ) : (
                  <>
                    <span className="hc-num hc-num-empty tnum">{par ?? '—'}</span>
                    <span className="hc-rel">tap to enter</span>
                  </>
                )}
              </div>

              <button
                type="button"
                className="hc-step"
                aria-label={`One more for ${scramble ? 'the foursome' : s.name}`}
                onClick={() => bump(s, +1)}
                disabled={s.value != null && s.value >= MAX_GROSS}
              >
                +
              </button>
            </div>

            {s.value != null ? (
              <button
                type="button"
                className="hc-clear"
                onClick={() => onScore(s.entrantId, null)}
              >
                Clear this hole
              </button>
            ) : null}
          </div>
        );
      })}

      {scramble ? (
        <p className="hc-note">
          One ball, one number &mdash; it goes on the card for everybody in the foursome. In a
          scramble you all made it.
        </p>
      ) : null}

      {upcoming ? (
        <div className="hc-upcoming">
          <span className="hc-upcoming-lead">Coming up &mdash; hole {upcoming.holeNumber}:</span>{' '}
          {upcoming.labels.join(' · ')}
        </div>
      ) : null}
    </div>
  );
}

const CSS = `
.hc {
  background: var(--paper); color: var(--ink);
  border: 1px solid var(--paper-border); border-radius: var(--radius);
  box-shadow: 0 5px 12px rgba(0,0,0,.35);
  padding: 14px; margin: 0;
}
.hc-head {
  display: flex; align-items: flex-end; justify-content: space-between;
  gap: 12px; padding-bottom: 10px; border-bottom: 1px solid var(--paper-border);
}
.hc-hole { display: flex; align-items: baseline; gap: 8px; }
.hc-hole-word {
  font-size: 13px; font-weight: 700; text-transform: uppercase;
  letter-spacing: .8px; color: var(--ink-dim);
}
.hc-hole-num { font-size: 46px; font-weight: 800; line-height: 1; }
.hc-head-right { text-align: right; }
.hc-par { font-size: 20px; font-weight: 800; }
.hc-pos { font-size: 12px; color: var(--ink-dim); }

/* Contest — brass, because it is the one thing on this screen worth interrupting for. */
.hc-contest {
  margin-top: 12px; padding: 12px;
  border: 2px solid var(--brass); border-radius: 8px;
  background: rgba(201,162,39,.12);
}
.hc-contest-head { display: flex; align-items: center; gap: 8px; }
.hc-contest-kind {
  font-size: 15px; font-weight: 800; text-transform: uppercase; letter-spacing: .6px;
}
.hc-contest-label { font-size: 14px; margin-top: 2px; }
.hc-claim-current { margin-top: 8px; font-size: 16px; }
.hc-claim-caveat { font-size: 12px; color: var(--ink-dim); margin-top: 4px; line-height: 1.35; }
.hc-claim-open {
  margin-top: 10px; width: 100%; min-height: 52px;
  background: transparent; color: var(--ink); border: 1px solid var(--brass); font-weight: 700;
}
.hc-claim-form { margin-top: 10px; }
.hc-claim-form select, .hc-claim-form input { min-height: 52px; }
.hc-claim-form label { margin-top: 8px; }
.hc-claim-actions { display: flex; gap: 8px; margin-top: 12px; flex-wrap: wrap; }
.hc-claim-actions button { flex: 1 1 auto; min-height: 52px; }

/* The unsaved marker. Small on purpose — it is information, not an alarm. */
.hc-dot {
  display: inline-block; width: 9px; height: 9px; border-radius: 50%;
  background: var(--brass); box-shadow: 0 0 0 2px rgba(201,162,39,.3);
}
.hc-unsaved {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 12px; font-weight: 600; color: var(--ink-dim);
}

.hc-line { padding-top: 14px; }
.hc-line + .hc-line { border-top: 1px dashed var(--paper-border); margin-top: 4px; }
.hc-line-name {
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
  min-height: 20px;
}
.hc-name {
  font-size: 16px; font-weight: 800; letter-spacing: .3px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.hc-stepper {
  margin-top: 8px; display: grid; grid-template-columns: 1fr minmax(96px, 1.1fr) 1fr;
  align-items: stretch; gap: 10px;
}
.hc-step {
  min-height: 76px; font-size: 34px; font-weight: 800; line-height: 1;
  background: var(--brass); color: #1C1400; border: none; border-radius: 10px;
  -webkit-tap-highlight-color: transparent; touch-action: manipulation;
  user-select: none;
}
.hc-step:active:not(:disabled) { transform: translateY(1px); filter: brightness(.94); }
.hc-value {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  background: #fff; border: 1px solid var(--paper-border); border-radius: 10px; padding: 4px;
}
.hc-num { font-size: 46px; font-weight: 800; line-height: 1.05; }
.hc-num-empty { color: var(--paper-border); }
.hc-rel { font-size: 12px; font-weight: 700; color: var(--ink-dim); margin-top: 2px; }
.hc-clear {
  margin-top: 10px; width: 100%; min-height: 44px;
  background: transparent; color: var(--ink-dim); border: 1px solid var(--paper-border);
  font-size: 14px; font-weight: 700;
}
.hc-note { font-size: 12px; color: var(--ink-dim); margin: 12px 0 0; line-height: 1.4; }
.hc-upcoming {
  margin-top: 12px; padding-top: 10px; border-top: 1px solid var(--paper-border);
  font-size: 13px; color: var(--ink-dim);
}
.hc-upcoming-lead { font-weight: 800; color: var(--ink); }
`;
